import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Lock, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ERROR_MESSAGES = {
  oauth: "Sign in was cancelled or Google didn't return a valid response. Please try again.",
  email: "Google didn't return a verified email address. Try a different Google account.",
  unconfigured: "Google Sign-In isn't configured on the server yet. Please contact the site owner.",
};

export default function AdminLogin() {
  const { login, user, initialized } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorKey = searchParams.get("error");
  const errorMessage = errorKey ? ERROR_MESSAGES[errorKey] || "Sign in didn't complete." : null;

  useEffect(() => {
    if (initialized && user) navigate("/admin", { replace: true });
  }, [initialized, user, navigate]);

  return (
    <div
      data-testid="page-admin-login"
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#FAF6EC" }}
    >
      <div className="w-full max-w-5xl grid gap-10 md:grid-cols-[1.1fr_1fr] items-center">
        <div>
          <Link
            to="/"
            className="font-script text-3xl"
            style={{ color: "#C9A84C" }}
            data-testid="admin-login-back-home"
          >
            The Life Refinery
          </Link>
          <h1
            className="font-serif mt-4 text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
            style={{ color: "#1B4332" }}
          >
            Welcome back, Priya.
          </h1>
          <p className="mt-5 max-w-lg text-base md:text-lg leading-relaxed" style={{ color: "#2A2621" }}>
            Sign in with your Google account to unlock the admin portal. Only invited emails may
            enter &mdash; anything else lands on an &ldquo;unauthorised&rdquo; page and no data
            is exposed.
          </p>

          <div className="mt-8 grid gap-4 max-w-md">
            <FeatureRow icon={<ShieldCheck size={16} />} label="Protected access" value="Google Sign-In &mdash; invite only" />
            <FeatureRow icon={<Lock size={16} />} label="Held gently" value="Nothing is ever shared outside this space" />
          </div>
        </div>

        <div
          data-testid="admin-signin-card"
          className="rounded-3xl border p-8 md:p-10"
          style={{
            background: "#fff",
            borderColor: "rgba(201,168,76,0.55)",
            boxShadow: "0 40px 80px -40px rgba(27,67,50,0.4)",
          }}
        >
          <span className="eyebrow">Sign in</span>
          <h2 className="font-serif mt-3 text-2xl md:text-3xl leading-tight" style={{ color: "#1B4332" }}>
            Continue with Google
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "#2A2621" }}>
            You&rsquo;ll be sent to Google&rsquo;s secure sign-in and returned here in a moment.
          </p>

          {errorMessage && (
            <div
              data-testid="admin-login-error"
              className="mt-6 flex items-start gap-3 rounded-xl border p-4"
              style={{ background: "#FBEDEA", borderColor: "#E7BFB4", color: "#8A2A2A" }}
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span className="text-sm">{errorMessage}</span>
            </div>
          )}

          <button
            type="button"
            onClick={login}
            data-testid="admin-google-signin"
            className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full border px-6 py-3 text-sm font-medium tracking-wide transition-all hover:shadow-md"
            style={{
              background: "#ffffff",
              borderColor: "rgba(27,67,50,0.2)",
              color: "#2A2621",
            }}
            aria-label="Sign in with Google"
          >
            <GoogleGlyph />
            Sign in with Google
          </button>

          <p className="mt-6 text-xs" style={{ color: "#6E6759" }}>
            By signing in you agree that the site&rsquo;s cookies will be used to keep you logged in
            for 7 days. You can sign out any time.
          </p>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-4">
      <span
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border"
        style={{ borderColor: "#C9A84C", color: "#1B4332", background: "#FAF6EC" }}
      >
        {icon}
      </span>
      <div>
        <div className="text-[11px] tracking-[0.24em] uppercase" style={{ color: "#6E6759" }}>{label}</div>
        <div className="font-serif text-lg" style={{ color: "#1B4332" }} dangerouslySetInnerHTML={{ __html: value }} />
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
